import {
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    HostBinding,
    Renderer2,
    NgZone,
    OnDestroy,
    input,
    TemplateRef,
    inject,
    linkedSignal,
    output,
} from '@angular/core';
import { DragDropService } from '../services/dragdrop.service';
import { DomHelper } from '../shared/dom-helper';

@Directive({
    selector: '[appDraggable]',
})
/**
 * Makes an element draggable by adding the draggable html attribute
 */
export class Draggable implements OnInit, OnDestroy {
    protected el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private dragDropService = inject(DragDropService);
    private zone = inject(NgZone);

    /**
     * The data that will be avaliable to the droppable directive on its `onDrop()` event.
     */
    readonly dragData = input(undefined);
    readonly dragDataComputed = linkedSignal(this.dragData);

    /**
     * Optionally supply a function that will be called when the drag event is triggered.
     */
    readonly dragDataFunction = input<Function>(undefined);

    /**
     * The selector that defines the drag Handle.
     * If defined drag will only be allowed if dragged from the selector element.
     */
    readonly dragHandle = input<string>(undefined);

    /**
     * Currently not used
     */
    readonly dragEffect = input('move');

    /**
     * Defines compatible drag drop pairs. Values must match both in draggable and droppable.dropScope.
     */
    readonly dragScope = input<string | Array<string>>('default');

    /**
     * The CSS class applied to a draggable element. If a dragHandle is defined then its applied to that handle
     * element only. By default it is used to change the mouse over pointer.
     */
    readonly dragHandleClass = input('drag-handle');

    /**
     * CSS class applied on the source draggable element while being dragged.
     */
    readonly dragClass = input('drag-border');

    /**
     * CSS class applied on the drag ghost when being dragged.
     */
    readonly dragTransitClass = input('drag-transit');

    /**
     * The url to image that will be used as custom drag image when the draggable is being dragged.
     */
    @Input() set dragImage(value: string) {
        this._dragImage = value;
        this.dragImageElement = new Image();
        this.dragImageElement.src = this.dragImage;
    }

    get dragImage() {
        return this._dragImage;
    }

    dragPreview = input<TemplateRef<unknown>>();

    /**
     * Defines if drag is enabled. `true` by default.
     */
    @HostBinding('draggable')
    @Input()
    set dragEnabled(value: boolean) {
        this._dragEnabled = value;
        this.applyDragHandleClass();
    }

    get dragEnabled() {
        return this._dragEnabled;
    }

    /**
     * Event fired when Drag is started
     */
    readonly dragStart = output<any>();

    /**
     * Event fired while the element is being dragged
     */
    readonly dragging = output<any>();

    /**
     * Event fired when drag ends
     */
    readonly dragEnd = output<any>();

    /**
     * @private
     * Keeps track of mouse over element that is used to determine drag handles
     */
    mouseDownElement: any;

    /**
     * @private
     * Backing field for the dragEnabled property
     */
    _dragEnabled = true;

    /**
     * @private
     * Backing field for the dragImage property
     */
    _dragImage: string;

    /**
     * @private
     * Image element for the dragImage
     */
    dragImageElement: HTMLImageElement;

    /**
     * @private
     * Function for unbinding the drag listener
     */
    unbindDragListener: Function;

    ngOnInit() {
        this.applyDragHandleClass();
    }

    ngOnDestroy() {
        this.unbindDragListeners();
    }

    @HostListener('dragstart', ['$event'])
    onDragStart(e: DragEvent) {
        if (this.allowDrag()) {
            // This is a kludgy approach to apply CSS to the drag helper element when an image is being dragged.
            DomHelper.addClass(this.el, this.dragTransitClass());
            setTimeout(() => {
                DomHelper.addClass(this.el, this.dragClass());
                DomHelper.removeClass(this.el, this.dragTransitClass());
            }, 10);

            const dragDataFunction = this.dragDataFunction();
            if (dragDataFunction) {
                this.dragDataComputed.set(dragDataFunction());
            }

            this.dragDropService.dragData = this.dragDataComputed();
            this.dragDropService.scope = this.dragScope();

            // Firefox requires setData() to be called otherwise the drag does not work.
            // We don't use setData() to transfer data anymore so this is just a dummy call.
            if (e.dataTransfer != null) {
                e.dataTransfer.setData('text', '');
            }

            if (this.dragPreview()) {
                console.log('use drag preview', this.dragPreview());
                const preview = this.dragPreview();
                console.log('items', dragDataFunction());
                const previewElementView = preview.createEmbeddedView({ items: dragDataFunction() });
                previewElementView.detectChanges();
                console.log('preview el view', previewElementView);
                const previewElement = previewElementView.rootNodes[0];
                console.log('preview el', previewElement);
                const previewContainer = document.createElement('div');
                previewContainer.id = 'drag-preview-container';
                previewContainer.appendChild(previewElement);
                console.log('previewContainer', previewContainer);
                document.body.appendChild(previewContainer);
                e.dataTransfer.setDragImage(previewContainer, 0, 0);
            } else if (this.dragImage) {
                e.dataTransfer.setDragImage(this.dragImageElement, 0, 0);
            }

            e.stopPropagation();
            this.dragStart.emit(e);
            this.dragDropService.onDragStart.next();

            // setTimeout(() => {
            //     debugger;
            // }, 1000);

            this.zone.runOutsideAngular(() => {
                this.unbindDragListener = this.renderer.listen(this.el.nativeElement, 'drag', (dragEvent) => {
                    this.handleDrag(dragEvent);
                });
            });
        } else {
            e.preventDefault();
        }
    }

    handleDrag(e) {
        this.dragging.emit(e);
    }

    @HostListener('dragend', ['$event'])
    handleDragEnd(e) {
        this.unbindDragListeners();
        // document.getElementById('drag-preview-container')?.remove();
        DomHelper.removeClass(this.el, this.dragClass());
        this.dragDropService.onDragEnd.next();
        this.dragEnd.emit(e);
        e.stopPropagation();
        e.preventDefault();
    }

    @HostListener('mousedown', ['$event'])
    @HostListener('touchstart', ['$event'])
    mousedown(e) {
        this.mouseDownElement = e.target;
    }

    private allowDrag() {
        const dragHandle = this.dragHandle();
        if (dragHandle) {
            return DomHelper.matches(this.mouseDownElement, dragHandle) && this.dragEnabled;
        } else {
            return this.dragEnabled;
        }
    }

    private applyDragHandleClass() {
        let dragElement = this.getDragHandleElement();

        if (!dragElement) {
            return;
        }

        if (this.dragEnabled) {
            DomHelper.addClass(dragElement, this.dragHandleClass());
        } else {
            DomHelper.removeClass(this.el, this.dragHandleClass());
        }
    }

    private getDragHandleElement() {
        let dragElement = this.el;
        const dragHandle = this.dragHandle();
        if (dragHandle) {
            dragElement = this.el.nativeElement.querySelector(dragHandle);
        }

        return dragElement;
    }

    unbindDragListeners() {
        if (this.unbindDragListener) {
            this.unbindDragListener();
        }
    }
}
